// Supabase client (browser).
//
// Reads URL + anon key from build-time Vite env vars. NEVER imports the
// service-role key — that's server-only and lives in Cloudflare Worker env.

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

// Strip invisible characters that Windows tooling can smuggle into env values:
// U+FEFF (BOM / zero-width no-break space) and U+200B (zero-width space). A
// single leading BOM on the anon key silently breaks every Supabase request
// with a 401 — see reference_windows_bom_secret_trap. clean() makes the client
// robust to that regardless of how the value was set (inline .env BOM, CF
// dashboard paste, etc.).
// Built from char codes so no invisible characters appear in this source file.
const INVISIBLE_RE = new RegExp(
  "[" + [0xfeff, 0x200b, 0x200c, 0x200d].map((c) => String.fromCharCode(c)).join("") + "]",
  "g",
);
const clean = (v: string | undefined): string | undefined =>
  v?.replace(INVISIBLE_RE, "").trim();

const url = clean(import.meta.env.VITE_SUPABASE_URL);
const anonKey = clean(import.meta.env.VITE_SUPABASE_ANON_KEY);

/** The project URL and public anon key (both public; they ship in the bundle). */
export const SUPABASE_URL = url ?? null;
export const SUPABASE_ANON_KEY = anonKey ?? null;

if (!url || !anonKey) {
  // Loud failure in dev; silent fallback in prod-build would mask config bugs.
  // The deploy pipeline must set both VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY.
  console.warn(
    "[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing — auth + DB calls will fail",
  );
}

export const supabase: SupabaseClient = createClient(
  url ?? "https://placeholder.supabase.co",
  anonKey ?? "placeholder-anon-key",
  {
    auth: {
      // Persist the session in localStorage so refresh doesn't kick users out.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);
